/**
 * A conta da quitação no front — a prévia antes de confirmar (16/09).
 *
 * Cópia de `backend/src/services/debt/quitacao.ts`, presa a ele pela tabela
 * `shared/quitacao.casos.json`, que as duas suítes leem. A tela mostra "você
 * economizou R$ X" ANTES de gravar; se a conta daqui divergisse da do
 * servidor, ela confirmaria um número e o histórico guardaria outro.
 */

export interface DividaDaConta {
  installments: number;
  installmentValue: number;
  /** 'YYYY-MM-DD' da primeira parcela. */
  firstInstallmentDate: string;
}

export interface PedidoDaConta {
  valorPago: number;
  modo: 'parcelas' | 'valor';
  parcelasRestantes?: number;
  valorParcela?: number;
}

export interface FotoDaDivida {
  parcelasRestantes: number;
  valorParcela: number;
  total: number;
}

export interface ResultadoDaQuitacao {
  antes: FotoDaDivida;
  depois: FotoDaDivida & { installments: number };
  economia: number;
  quitada: boolean;
}

export type VereditoDaQuitacao =
  | { ok: true; resultado: ResultadoDaQuitacao }
  | { ok: false; motivo: string };

const centavos = (v: number): number => Math.round(v * 100) / 100;

/** Mesma regra de `backend/src/services/debt.ts`: o mês de hoje conta como pago. */
export const parcelasPagas = (d: DividaDaConta, hoje: Date): number => {
  const [y, m] = d.firstInstallmentDate.split('-').map(Number);
  if (!y || !m) return 0;
  const decorridos = (hoje.getFullYear() - y) * 12 + (hoje.getMonth() - (m - 1)) + 1;
  return Math.max(0, Math.min(decorridos, d.installments));
};

export const calcularQuitacao = (d: DividaDaConta, hoje: Date, p: PedidoDaConta): VereditoDaQuitacao => {
  if (!Number.isFinite(p.valorPago) || p.valorPago <= 0) {
    return { ok: false, motivo: 'Diga quanto você pagou na quitação.' };
  }
  const pagas = parcelasPagas(d, hoje);
  const restantesAntes = Math.max(d.installments - pagas, 0);
  if (restantesAntes === 0) {
    return { ok: false, motivo: 'Essa dívida já não tem parcelas a pagar.' };
  }
  const antes: FotoDaDivida = {
    parcelasRestantes: restantesAntes,
    valorParcela: d.installmentValue,
    total: centavos(restantesAntes * d.installmentValue),
  };

  let restantes: number;
  let valorParcela: number;
  if (p.modo === 'parcelas') {
    const n = p.parcelasRestantes;
    if (n === undefined || !Number.isInteger(n) || n < 0) {
      return { ok: false, motivo: 'Diga quantas parcelas faltam agora (0 se quitou tudo).' };
    }
    if (n === restantesAntes) {
      return { ok: false, motivo: `Continuam ${n} parcelas. Se o que mudou foi o valor da parcela, escolha "parcela menor".` };
    }
    if (n > restantesAntes) {
      return { ok: false, motivo: `Hoje faltam ${restantesAntes} parcelas; depois de quitar, tem que faltar menos.` };
    }
    restantes = n;
    valorParcela = d.installmentValue;
  } else {
    const v = p.valorParcela;
    if (v === undefined || !Number.isFinite(v) || v <= 0) {
      return { ok: false, motivo: 'Diga o valor da parcela nova. Se quitou tudo, escolha "menos parcelas" e 0.' };
    }
    if (v >= d.installmentValue) {
      return { ok: false, motivo: 'A parcela nova tem que ser menor que a de hoje.' };
    }
    restantes = restantesAntes;
    valorParcela = centavos(v);
  }

  const total = centavos(restantes * valorParcela);
  return {
    ok: true,
    resultado: {
      antes,
      depois: { parcelasRestantes: restantes, valorParcela, total, installments: pagas + restantes },
      economia: centavos(antes.total - total - p.valorPago),
      quitada: restantes === 0,
    },
  };
};
